import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TurnStileDarkPlaceholder from '../../assets/images/TurnStileDarkPlaceholder.png';
import TurnStileLightPlaceholder from '../../assets/images/TurnStileLightPlaceholder.png';
import useTheme from '../../../../hooks/ui/useTheme';
import CircularProgress from '@mui/material/CircularProgress';
import { ITurnstileVerifyPayload } from '../../../../components-library/auth/turnstile/types';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import { AnimatePresence, motion } from 'framer-motion';
import * as Sentry from '@sentry/react';
import classnames from 'classnames';
import { PROGRESS_INTERVAL_MS, TURNSTILE_TIMEOUT_MS } from '../../../../components-library/auth/turnstile/Turnstile';

const MessageType = {
  TurnStileToken: 'turnstile-token',
  ParentOrigin: 'parent-origin',
  TurnStileLoaded: 'turnstile-loaded',
  Verifying: 'verifying',
  Resize: 'resize',
};

interface TurnStileIframeProps {
  onVerify: (payload: ITurnstileVerifyPayload) => void;
  className?: string;
}

const DEFAULT_IFRAME_HEIGHT = 65;
const MIN_IFRAME_HEIGHT = 65;
// The widget's tallest legitimate state is hCaptcha's opened challenge (550).
const MAX_IFRAME_HEIGHT = 600;

/**
 * Origin of the captcha iframe: the only sender whose messages may be acted on,
 * and the only target the handshake may be posted to. Resolved once from the
 * configured URL — an unparseable or missing value yields null, which rejects
 * every message rather than trusting all of them.
 */
const TURNSTILE_ORIGIN = ((): string | null => {
  try {
    return new URL(import.meta.env.VITE_STOREFRONT_TURNSTILE_DOMAIN).origin;
  } catch {
    return null;
  }
})();

let missingWrapperUrlReported = false;

/**
 * Without a wrapper URL there is nothing to frame. The src would read `undefined/?…`, a relative
 * path that loads this shop's own SPA inside the captcha box, and every message it posted would be
 * rejected. The value is set per environment in Vercel, not in a committed .env file, so a build
 * without it is misconfigured: say so once per page load, not once per form that renders the
 * widget. The console says it too, since Sentry only runs in production builds.
 */
function reportMissingWrapperUrl(): void {
  if (missingWrapperUrlReported) {
    return;
  }

  missingWrapperUrlReported = true;

  const message = '[captcha] VITE_STOREFRONT_TURNSTILE_DOMAIN is unset or not a URL, so no captcha can be framed';
  const configured = import.meta.env.VITE_STOREFRONT_TURNSTILE_DOMAIN ?? null;

  console.error(message, { configured });
  Sentry.captureMessage(message, {
    level: 'error',
    tags: { flow: 'captcha' },
    fingerprint: ['captcha', 'wrapper_url_missing'],
    contexts: { captcha: { configured } },
  });
}

const TurnStile = ({ onVerify, className }: TurnStileIframeProps) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [iframeHeight, setIframeHeight] = useState<number>(DEFAULT_IFRAME_HEIGHT);
  const intervalRef = useRef<number | null>(null);

  const stopProgress = (): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsVerifying(false);
    setProgress(0);
  };

  const startProgress = (): void => {
    setProgress(0);
    setIsVerifying(true);

    const startTime = Date.now();

    intervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const value = Math.min((elapsed / TURNSTILE_TIMEOUT_MS) * 100, 100);
      setProgress(value);
    }, PROGRESS_INTERVAL_MS);
  };

  const handleVerifying = (isVerifying: boolean): void => {
    isVerifying ? startProgress() : stopProgress();
  };

  const handleTurnStileLoaded = (): void => {
    setTimeout(() => setLoading(false), 500);
  };

  const handleTurnStileToken = (payload: ITurnstileVerifyPayload): void => {
    stopProgress();
    onVerify(payload);
  };

  const handleResize = (payload: { height: number }): void => {
    const height = Number(payload?.height);

    if (!Number.isFinite(height)) {
      return;
    }

    setIframeHeight(Math.min(Math.max(height, MIN_IFRAME_HEIGHT), MAX_IFRAME_HEIGHT));
  };

  const handleMessage = (event: MessageEvent<{ type: string; payload?: any }>) => {
    // Accepting a turnstile-token is a trust decision, so only the widget
    // iframe may make it. This listener is on window, so without these two
    // checks any frame, opener or popup that can reach this page could post a
    // token and skip the captcha outright.
    if (!TURNSTILE_ORIGIN || event.origin !== TURNSTILE_ORIGIN) {
      return;
    }

    // Origin alone is not enough: any other document on the same origin would
    // pass it. Pin the sender to this component's own iframe.
    if (event.source !== iframeRef.current?.contentWindow) {
      return;
    }

    switch (event?.data?.type) {
      case MessageType.Verifying:
        handleVerifying(event.data.payload?.isVerifying);
        break;
      case MessageType.TurnStileLoaded:
        handleTurnStileLoaded();
        break;
      case MessageType.TurnStileToken:
        handleTurnStileToken(event.data.payload as ITurnstileVerifyPayload);
        break;
      case MessageType.Resize:
        handleResize(event.data.payload as { height: number });
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!TURNSTILE_ORIGIN) {
      reportMissingWrapperUrl();
    }
  }, []);

  const renderPlaceholder = useMemo(
    () =>
      loading && (
        <div className="absolute inset-0 bg-brand-background-primary z-10 h-[65px]">
          <div className="flex items-center h-full w-[300px] relative">
            <img
              src={theme.isDarkMode ? TurnStileDarkPlaceholder : TurnStileLightPlaceholder}
              alt="TurnStile"
              className="w-full h-full absolute inset-0"
            />
            <div className="relative px-[17px] flex items-center gap-[10.5px] w-full">
              <CircularProgress size={30} sx={{ color: '#1A9F31' }} />
              <p className="text-[#272727] dark:text-white text-[13px] font-normal">Verifying…</p>
            </div>
          </div>
        </div>
      ),
    [loading, theme.isDarkMode],
  );

  const progressBar = useMemo(
    () => (
      <AnimatePresence>
        {isVerifying && (
          <motion.div
            key="turnstile-progress"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'linear' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    borderRadius: '9999px',
                    height: 4,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: '9999px',
                    },
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <p className="text-xs">{`${Math.round(progress)}%`}</p>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    ),
    [isVerifying, progress],
  );

  const queryParams = {
    language: i18n.language,
    theme: theme.isDarkMode ? 'dark' : 'light',
    type: MessageType.TurnStileToken,
  };

  // The parsed origin, not the raw value: a trailing slash in the variable would otherwise read `//?`.
  const url = `${TURNSTILE_ORIGIN ?? ''}/?${new URLSearchParams(queryParams).toString()}`;

  const sendMessageToIframe = (): void => {
    if (!TURNSTILE_ORIGIN) {
      return;
    }

    setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage({ type: MessageType.ParentOrigin }, TURNSTILE_ORIGIN);
    }, 200);
  };

  const widget = useMemo(
    () => (
      <iframe
        ref={iframeRef}
        src={url}
        className={'w-full scrollbar-hide'}
        referrerPolicy="no-referrer-when-downgrade"
        style={{ border: 'none', overflow: 'hidden', height: iframeHeight }}
        onLoad={sendMessageToIframe}
      />
    ),
    [url, iframeHeight],
  );

  // No box at all rather than one framing this shop's own SPA; see reportMissingWrapperUrl.
  if (!TURNSTILE_ORIGIN) {
    return null;
  }

  return (
    <div className={classnames('relative w-full h-fit mt-md', className)}>
      {progressBar}
      <div className="relative w-full h-fit">
        {widget}
        {renderPlaceholder}
      </div>
    </div>
  );
};

export default TurnStile;
